/* eslint-disable react/prop-types */
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Typography,
  Chip,
  Tooltip,
  Paper,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import { useState } from "react";

export interface Branch {
  address: string;
  emailAddress: string;
  primaryPhoneNumber: string;
  faxNumber?: string;
}

export interface PracticeUser {
  id: string;
  accountId: string;
  email: string;
  name: string;
  phoneNumber: string;
  role: string;
  npi?: string;
  branches: Branch[];
  specialties: string[];
  status: "active" | "inactive";
  addedDate: string;
}

interface PracticeUserTableProps {
  practiceUsers: PracticeUser[];
  loading: boolean;
  sortBy: string;
  sortOrder: "asc" | "desc";
  onSortChange: (sortBy: string, sortOrder: "asc" | "desc") => void;
  onUserClick: (userId: string) => void;
  onDeleteUser: (userId: string, userName: string) => void;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
  };
}

const PracticeUserTable: React.FC<PracticeUserTableProps> = ({
  practiceUsers,
  loading,
  sortBy,
  sortOrder,
  onSortChange,
  onUserClick,
  onDeleteUser,
  pagination,
}) => {
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  const handleSort = (column: string) => {
    const newOrder = sortBy === column && sortOrder === "asc" ? "desc" : "asc";
    onSortChange(column, newOrder);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  const renderBranches = (branches: Branch[]) => {
    if (branches.length === 0) return "-";

    const maxDisplay = 3;
    const visibleBranches = branches.slice(0, maxDisplay);
    const remainingCount = branches.length - maxDisplay;

    return (
      <Box className="flex flex-wrap gap-1">
        {visibleBranches.map((branch, index) => (
          <Chip
            key={`branch-${branch.address}-${index}`}
            label={branch.address}
            size="small"
            className="text-xs"
            sx={{
              backgroundColor: "#F3F4F6",
              color: "#374151",
              "& .MuiChip-label": {
                fontSize: "0.75rem",
                padding: "0 8px",
              },
            }}
          />
        ))}
        {remainingCount > 0 && (
          <Tooltip
            title={
              <Box>
                <Typography variant="body2" className="font-medium mb-1">
                  All Branches:
                </Typography>
                {branches.map((branch, index) => (
                  <Box
                    key={`branch-tooltip-${branch.address}-${index}`}
                    className="mb-2"
                  >
                    <Typography variant="body2" className="font-medium">
                      • {branch.address}
                    </Typography>
                    <Typography variant="caption" className="text-gray-600">
                      {branch.emailAddress} • {branch.primaryPhoneNumber}
                    </Typography>
                  </Box>
                ))}
              </Box>
            }
            arrow
            placement="top"
            slotProps={{
              tooltip: {
                sx: {
                  backgroundColor: "#ffffff",
                  color: "#374151",
                  border: "1px solid #E5E7EB",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  "& .MuiTooltip-arrow": {
                    color: "#ffffff",
                    "&::before": {
                      border: "1px solid #E5E7EB",
                    },
                  },
                },
              },
            }}
          >
            <Chip
              label={`(+${remainingCount} more)`}
              size="small"
              className="text-xs cursor-help"
              sx={{
                backgroundColor: "#E5E7EB",
                color: "#6B7280",
                "& .MuiChip-label": {
                  fontSize: "0.75rem",
                  padding: "0 8px",
                },
              }}
            />
          </Tooltip>
        )}
      </Box>
    );
  };

  const renderSpecialties = (specialties: string[]) => {
    if (specialties.length === 0) return "-";

    const maxDisplay = 5;
    const visibleSpecialties = specialties.slice(0, maxDisplay);
    const remainingCount = specialties.length - maxDisplay;

    return (
      <Box className="flex flex-wrap gap-1">
        {visibleSpecialties.map((specialty) => (
          <Chip
            key={specialty}
            label={specialty}
            size="small"
            className="text-xs"
            sx={{
              backgroundColor: "#DBEAFE",
              color: "#1E40AF",
              "& .MuiChip-label": {
                fontSize: "0.75rem",
                padding: "0 8px",
              },
            }}
          />
        ))}
        {remainingCount > 0 && (
          <Tooltip
            title={
              <Box>
                <Typography variant="body2" className="font-medium mb-1">
                  All Specialties:
                </Typography>
                {specialties.map((specialty) => (
                  <Typography key={specialty} variant="body2" className="block">
                    • {specialty}
                  </Typography>
                ))}
              </Box>
            }
            arrow
            placement="top"
            slotProps={{
              tooltip: {
                sx: {
                  backgroundColor: "#ffffff",
                  color: "#374151",
                  border: "1px solid #E5E7EB",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  "& .MuiTooltip-arrow": {
                    color: "#ffffff",
                    "&::before": {
                      border: "1px solid #E5E7EB",
                    },
                  },
                },
              },
            }}
          >
            <Chip
              label={`(+${remainingCount} more)`}
              size="small"
              className="text-xs cursor-help"
              sx={{
                backgroundColor: "#E5E7EB",
                color: "#6B7280",
                "& .MuiChip-label": {
                  fontSize: "0.75rem",
                  padding: "0 8px",
                },
              }}
            />
          </Tooltip>
        )}
      </Box>
    );
  };

  const getStatusChip = (status: "active" | "inactive") => {
    return (
      <Chip
        label={status.charAt(0).toUpperCase() + status.slice(1)}
        size="small"
        sx={{
          backgroundColor: status === "active" ? "#D1FAE5" : "#FEE2E2",
          color: status === "active" ? "#065F46" : "#991B1B",
          "& .MuiChip-label": {
            fontSize: "0.75rem",
            fontWeight: 500,
          },
        }}
      />
    );
  };

  const columns = [
    { id: "email", label: "Email", sortable: true },
    { id: "name", label: "Name", sortable: true },
    { id: "phoneNumber", label: "Phone Number", sortable: true },
    { id: "role", label: "Role", sortable: false },
    { id: "npi", label: "NPI", sortable: true },
    { id: "branches", label: "Branch", sortable: false },
    { id: "specialties", label: "Specialty", sortable: false },
    { id: "status", label: "Status", sortable: false },
    { id: "addedDate", label: "Added Date", sortable: true },
    { id: "actions", label: "", sortable: false },
  ];

  if (loading) {
    return (
      <Box className="flex justify-center items-center py-8">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper className="shadow-sm border border-gray-200">
      <TableContainer>
        <Table
          sx={{
            "& td, & th": {
              borderRight: "1px solid #E5E7EB",
            },
            "& td:last-of-type, & th:last-of-type": {
              borderRight: "none",
            },
          }}
        >
          <TableHead>
            <TableRow className="bg-gray-50">
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  className="font-medium text-gray-900 py-3"
                  sx={{
                    borderBottom: "1px solid #E5E7EB",
                    "&:first-of-type": {
                      paddingLeft: "24px",
                    },
                    "&:last-of-type": {
                      paddingRight: "24px",
                    },
                  }}
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={sortBy === column.id}
                      direction={sortBy === column.id ? sortOrder : "asc"}
                      onClick={() => handleSort(column.id)}
                      className="font-medium"
                      sx={{
                        "& .MuiTableSortLabel-icon": {
                          color: "#6B7280",
                        },
                        "&.Mui-active": {
                          color: "#374151",
                        },
                        "&:hover": {
                          color: "#374151",
                        },
                      }}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    <span className="font-medium text-gray-900">
                      {column.label}
                    </span>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {practiceUsers.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="text-center py-8 text-gray-500"
                >
                  No staff found
                </TableCell>
              </TableRow>
            ) : (
              practiceUsers.map((user) => (
                <TableRow
                  key={user.id}
                  className={`cursor-pointer transition-colors ${
                    hoveredRow === user.id ? "bg-blue-50" : "hover:bg-gray-50"
                  }`}
                  onMouseEnter={() => setHoveredRow(user.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  sx={{
                    "&:hover": {
                      backgroundColor: "#F9FAFB",
                    },
                    "& td": {
                      borderBottom: "1px solid #F3F4F6",
                      padding: "12px 16px",
                    },
                    "& td:first-of-type": {
                      paddingLeft: "24px",
                    },
                    "& td:last-of-type": {
                      paddingRight: "24px",
                    },
                  }}
                >
                  <TableCell className="text-sm">{user.email}</TableCell>
                  <TableCell className="text-sm font-medium">
                    {user.name}
                  </TableCell>
                  <TableCell className="text-sm">{user.phoneNumber}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.role.replace("_", " ").toUpperCase()}
                      size="small"
                      className="text-xs"
                      sx={{
                        backgroundColor: "#F3F4F6",
                        color: "#374151",
                        "& .MuiChip-label": {
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell className="text-sm font-mono">
                    {user.npi || "-"}
                  </TableCell>
                  <TableCell>{renderBranches(user.branches)}</TableCell>
                  <TableCell>{renderSpecialties(user.specialties)}</TableCell>
                  <TableCell>{getStatusChip(user.status)}</TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {formatDate(user.addedDate)}
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <RowActions
                      onViewDetails={() => onUserClick(user.id)}
                      onDelete={() => onDeleteUser(user.id, user.name)}
                      disabledDelete={false}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={pagination.total}
        page={pagination.page - 1}
        onPageChange={(_, newPage) => pagination.onPageChange(newPage + 1)}
        rowsPerPage={pagination.pageSize}
        onRowsPerPageChange={(event) =>
          pagination.onPageSizeChange(parseInt(event.target.value, 10))
        }
        rowsPerPageOptions={[10, 20, 50]}
        className="border-t border-gray-200"
        sx={{
          "& .MuiTablePagination-toolbar": {
            paddingLeft: "24px",
            paddingRight: "24px",
          },
          "& .MuiTablePagination-selectLabel": {
            fontSize: "0.875rem",
            color: "#374151",
          },
          "& .MuiTablePagination-displayedRows": {
            fontSize: "0.875rem",
            color: "#374151",
          },
        }}
      />
    </Paper>
  );
};

export default PracticeUserTable;

type RowActionsProps = {
  onViewDetails: () => void;
  onDelete: () => void;
  disabledDelete?: boolean;
};

const RowActions: React.FC<RowActionsProps> = ({
  onViewDetails,
  onDelete,
  disabledDelete = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleView = () => {
    handleClose();
    onViewDetails();
  };

  const handleDelete = () => {
    handleClose();
    if (!disabledDelete) onDelete();
  };

  return (
    <>
      <IconButton size="small" onClick={handleOpen}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleView}>View details</MenuItem>
        <MenuItem onClick={handleDelete} disabled={disabledDelete}>
          Delete
        </MenuItem>
      </Menu>
    </>
  );
};
